// import dependencies
import React, { Component, Fragment } from 'react';
import { reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import each from 'lodash/each';
import uniq from 'lodash/uniq';

import InputField from '~/common/components/InputField';
import Select from '~/common/components/Select';
import SuggestedCities from '~/views/Billing/components/SuggestedCities';

import fatalErrorModal from '~/common/utils/fatalErrorModal.js';
import { PAYMENT_ESTIMATE_GENERIC_ERROR, PAYMENT_ESTIMATE_MISMATCH_COUNTRY } from '~/common/utils/errorMessaging.js';
import { gtm, beginCheckoutFlowEvent, errorPurchaseEvent, errorFormEvent } from '~/common/utils/tracking';

// import stylesheet
import styles from './billing-address.css';

@inject(
    'offerSuiteStore',
    'convertToPaidStore',
    'formsStore',
    'billingInfoStore',
    'userLocationStore',
    'numberLockSessionStore',
    'suggestedCitiesStore'
)
@observer
export default class BillingAddress extends Component {
    state = {
        checkoutFlowBillingAddressInit: false
    };

    constructor(props) {
        super(props);
        const { billingInfoStore, formsStore, userLocationStore } = this.props;

        // get list of countries
        billingInfoStore.getCountries(() => {
            // check to see if the userLocation country code matches a country code from fetch countries
            const foundCountry = billingInfoStore.orderedCountries.find((country) => {
                return country.Code === userLocationStore.UserCountryCode;
            });
            // set countryCode to user location country code if found
            if (foundCountry) {
                formsStore.setFormData('CountryCode', foundCountry.Code);
                // If country has states get them
                if (foundCountry.HasStates) {
                    billingInfoStore.getStates(foundCountry.Code);
                    billingInfoStore.setCountryHasStates(true);
                } else {
                    billingInfoStore.setCountryHasStates(false);
                }
            }
        });

        // reset calculated total flag so there is no UI flash of old prices and recalculate
        this.resetOrderTotal(true);
    }

    resetOrderTotal(recalculateTaxes = false) {
        const { billingInfoStore, convertToPaidStore } = this.props;
        // grab observables depending on the flow
        const { calculatedTotal } = convertToPaidStore.isUserConvertingToPaid ? convertToPaidStore : billingInfoStore;
        // check if taxes have been calculated and if billing address is filled
        if (calculatedTotal) {
            if (convertToPaidStore.isUserConvertingToPaid) {
                convertToPaidStore.setOrderTotal({});
            } else {
                billingInfoStore.setOrderTotal({});
            }
            if (recalculateTaxes) {
                this.estimatePayment();
            }
        }
    }

    findSuggestedCities() {
        const { suggestedCitiesStore, formsStore } = this.props;

        suggestedCitiesStore.validateAddress(formsStore.ZipCode, (response) => {
            if (response.data.length > 0) {
                suggestedCitiesStore.setSuggestedCities(response.data);
                suggestedCitiesStore.setShowSuggestedCities(true);
            } else {
                // don't show suggested cities flyout
                suggestedCitiesStore.setSuggestedCities([]);
                suggestedCitiesStore.setShowSuggestedCities(false);
                // display error message
                this.genericBillingAddressError();
            }
        }, () => {
            // don't show suggested cities flyout
            suggestedCitiesStore.setSuggestedCities([]);
            suggestedCitiesStore.setShowSuggestedCities(false);
            // display error message
            this.genericBillingAddressError();
        });
    }

    billingInfoFields() {
        const { billingInfoStore } = this.props;
        // set fields to validate
        const fields = ['Street1', 'City', 'ZipCode', 'CountryCode'];

        // if the country has states add the field to the array
        if (billingInfoStore.countryHasStates) fields.push('StateCode');

        return fields;
    }

    setBillingAddressFieldsError() {
        const { formsStore } = this.props;
        const fields = this.billingInfoFields();

        fields.forEach((field) => {
            formsStore.setFieldError(field, ' ');
        });
    }

    genericBillingAddressError() {
        const { billingInfoStore, convertToPaidStore } = this.props;
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        this.setBillingAddressFieldsError();

        if (convertToPaidStore.isUserConvertingToPaid) {
            gtm(errorPurchaseEvent(PAYMENT_ESTIMATE_GENERIC_ERROR));
            convertToPaidStore.setEstimatePaymentError(PAYMENT_ESTIMATE_GENERIC_ERROR);
            convertToPaidStore.setInvalidAddress(true);
        } else {
            gtm(errorPurchaseEvent(PAYMENT_ESTIMATE_GENERIC_ERROR));
            billingInfoStore.setEstimatePaymentError(PAYMENT_ESTIMATE_GENERIC_ERROR);
            billingInfoStore.setInvalidAddress(true);
        }
    }

    estimatePayment = () => {
        const { billingInfoStore, convertToPaidStore, formsStore, suggestedCitiesStore, numberLockSessionStore } = this.props;
        const { selectedOfferSet } = this.props.offerSuiteStore;
        // grab observables depending on the flow
        const { calculatingTaxes } = convertToPaidStore.isUserConvertingToPaid ? convertToPaidStore : billingInfoStore;
        const fields = this.billingInfoFields();

        // check if any of the fields are empty
        if (!formsStore.areFieldsEmpty(fields) && !calculatingTaxes) {
            // clear up suggested cities
            suggestedCitiesStore.setShowSuggestedCities(false);
            suggestedCitiesStore.setSuggestedCities([]);
            suggestedCitiesStore.setSuggestedCityCurrentPage(1);
            // clear up any previous billing errors
            formsStore.removeValidationErrors(fields);
            if (convertToPaidStore.isUserConvertingToPaid) {
                // reset billing address error
                convertToPaidStore.setInvalidAddress(false);
                // reset billing info error
                convertToPaidStore.setEstimatePaymentError('');
            } else {
                // reset billing address error
                billingInfoStore.setInvalidAddress(false);
                // reset billing info error
                billingInfoStore.setEstimatePaymentError('');
            }

            // trial to paid
            if (convertToPaidStore.isUserConvertingToPaid) {
                convertToPaidStore.estimatePayment(
                    selectedOfferSet,
                    // location callback
                    () => {
                        gtm(errorFormEvent('buy-form', 'CountryCode,ZipCode', `${PAYMENT_ESTIMATE_MISMATCH_COUNTRY},invalid zipcode`));
                        // this gets called when invalid zipcode is entered
                        formsStore.setFieldError('CountryCode', PAYMENT_ESTIMATE_MISMATCH_COUNTRY);
                        formsStore.setFieldError('ZipCode', ' ');
                    },
                    () => {
                        this.findSuggestedCities();
                    }
                );
            } else {
                // direct purchase estimate payment
                billingInfoStore.estimatePayment(numberLockSessionStore.NLS, selectedOfferSet, (err) => {
                    // error response data
                    // TODO: institute deepGet here for safe error handling
                    const errorData = err.response.data;

                    this.resetOrderTotal();

                    if (typeof errorData === 'object' && errorData.ErrorType === 'ModelValidationError') {
                        const modelValidationErrors = [];
                        // create an array of 2004 and 2003 form errors
                        each(errorData.Details, (value) => {
                            if (value.ErrorCode === 2004 || value.ErrorCode === 2003) {
                                modelValidationErrors.push(value.Field.split('.').pop());
                            }
                        });
                        // set billing form field errors
                        each(uniq(modelValidationErrors), (value) => {
                            formsStore.setFieldError(value, ' ');
                        });
                        gtm(errorFormEvent('buy-form', modelValidationErrors.join(), 'invalid error'));
                        return billingInfoStore.setEstimatePaymentError(PAYMENT_ESTIMATE_GENERIC_ERROR);
                    }

                    // Fatal core errors, or logic problem with inputs
                    if (
                        typeof errorData === 'object' &&
                        (errorData.ErrorType === 'CoreError' || errorData.ErrorType === 'LogicalError') &&
                        errorData.Details[0] &&
                        typeof errorData.Details[0].ErrorCode === 'number'
                    ) {
                        // store error code to check against
                        const errorCode = errorData.Details[0].ErrorCode;

                        switch (errorCode) {
                            // if number is no longer in session
                            case 5006:
                            // break omitted
                            case 5007:
                                gtm(errorPurchaseEvent('number no longer in session'));
                                // open up number no longer in session modal
                                return fatalErrorModal();
                            // 5003 is address mismatch
                            case 5003:
                                return this.findSuggestedCities();
                            // invalid US zipcode
                            case 3005:
                            // invalid CA zipcode
                            // break omitted
                            case 3006:
                                gtm(errorFormEvent('buy-form', 'CountryCode,ZipCode', `${PAYMENT_ESTIMATE_MISMATCH_COUNTRY},invalid zipcode`));
                                formsStore.setFieldError('CountryCode', PAYMENT_ESTIMATE_MISMATCH_COUNTRY);
                                formsStore.setFieldError('ZipCode', ' ');

                                return billingInfoStore.setInvalidAddress(true);
                            // catch all error
                            default:
                                return this.genericBillingAddressError();
                        }
                    }
                    // set general message of error
                    return this.genericBillingAddressError();
                });
            }
        }
    };

    // hydrate coutries select input
    setCountries() {
        if (this.props.billingInfoStore.fetchedCountries) {
            return this.props.billingInfoStore.orderedCountries.map((country) => {
                return (
                    <option key={`country-${country.Code}`} value={country.Code}>{country.Name}</option>
                );
            });
        }

        return <option key="countries-loading" value="">Loading</option>;
    }

    selectCountry = () => {
        const { formsStore, billingInfoStore } = this.props;

        // clear previus selected state
        formsStore.setFormData('StateCode', '');
        // validate field
        formsStore.CountryCodeValidation();
        // set the country to no have to state until is it needed
        billingInfoStore.setCountryHasStates(false);
        // find the selected country in the
        if (!formsStore.doesFormHaveErrors(['CountryCode'])) {
            const country = billingInfoStore.orderedCountries.find((cntry) => {
                return cntry.Code === formsStore.CountryCode;
            });

            if (country.HasStates) {
                billingInfoStore.getStates(country.Code);
                billingInfoStore.setCountryHasStates(true);
            }
        }
    };

    selectState = () => {
        const { formsStore } = this.props;
        // validate field
        formsStore.StateCodeValidation();
        // estimate payment
        this.estimatePayment();
    };

    // hydrate states select input
    setStates() {
        if (this.props.billingInfoStore.fetchedStates) {
            return this.props.billingInfoStore.orderedStates.map((state) => {
                return (
                    <option
                        key={`state-${state.Code}`}
                        value={state.Code}
                    >{state.Name}</option>
                );
            });
        }

        return <option key="states-loading" value="">Loading</option>;
    }

    autoPaymentEstimate = reaction(
        () => this.props.convertToPaidStore.isUserConvertingToPaid && this.props.convertToPaidStore.timeoutErrorStep === 'payment estimate',
        () => {
            this.props.convertToPaidStore.setTimeoutErrorStep('');
            this.estimatePayment();
        }
    );

    // Checks if the first interaction of the form is made in this section.
    // Calls BeginCheckoutFlow event with step value.
    callBuyFormEvent() {
        const { formsStore, ignoreBeginCheckoutGtmCalled } = this.props;
        if (!formsStore.isFormInteracted()) {
            formsStore.setFormInteracted(true);
        }

        if (!this.state.checkoutFlowBillingAddressInit && !ignoreBeginCheckoutGtmCalled) {
            this.setState({ checkoutFlowBillingAddressInit: true });
            gtm(beginCheckoutFlowEvent(3));
        }
    }

    render() {
        const {
            formsStore: {
                Street1,
                Street1Error,
                City,
                CityError,
                StateCode,
                StateCodeError,
                CountryCode,
                CountryCodeError,
                ZipCode,
                ZipCodeError
            },
            billingInfoStore: {
                fetchedCountries,
                fetchedStates,
                countryHasStates
            }
        } = this.props;
        return (
            <Fragment>
                <InputField
                    allowedCharacters=" #,."
                    autoComplete="street-address"
                    dataTest="street-address-input"
                    defaultValue={Street1}
                    error={Street1Error}
                    id="street-address"
                    label="Billing Address"
                    name="Street1"
                    onBlur={this.estimatePayment}
                    onFocus={() => this.callBuyFormEvent()}
                    restrictCharacters="alphaNumericOnly"
                    type="text"
                    wrapperClass={styles.input}
                />
                <InputField
                    allowedCharacters=" "
                    autoComplete="address-level2"
                    dataTest="city-input"
                    defaultValue={City}
                    error={CityError}
                    id="city"
                    label="City"
                    name="City"
                    onBlur={this.estimatePayment}
                    onFocus={() => this.callBuyFormEvent()}
                    restrictCharacters="latinOnly"
                    type="text"
                    wrapperClass={styles.input}
                />
                <SuggestedCities estimatePayment={this.estimatePayment} />
                <Select
                    dataTest="state-input"
                    disabled={!fetchedStates || !countryHasStates}
                    error={StateCodeError}
                    id="state"
                    label="State"
                    name="StateCode"
                    onChange={this.selectState}
                    onFocus={() => this.callBuyFormEvent()}
                    selected={StateCode}
                    value={StateCode}
                >
                    {this.setStates()}
                </Select>
                <InputField
                    allowedCharacters=" -"
                    autoComplete="postal-code"
                    dataTest="zipcode-input"
                    defaultValue={ZipCode}
                    error={ZipCodeError}
                    id="zipcode"
                    label="Zip Code"
                    maxLength={12}
                    name="ZipCode"
                    onBlur={this.estimatePayment}
                    onFocus={() => this.callBuyFormEvent()}
                    restrictCharacters="alphaNumericOnly"
                    type="text"
                />
                <Select
                    dataTest="country-input"
                    disabled={!fetchedCountries}
                    error={CountryCodeError}
                    id="country"
                    label="Country"
                    name="CountryCode"
                    onChange={this.selectCountry}
                    onFocus={() => this.callBuyFormEvent()}
                    selected={CountryCode}
                    value={CountryCode}
                    wrapperClass={styles.input}
                >
                    {this.setCountries()}
                </Select>
            </Fragment>
        );
    }
}
